/**
 * ArticleBlocks — renderer for the block-based article body.
 *
 * Used by:
 *   - /articles/:id (public)
 *   - /panelist/articles/:id (logged-in patron)
 *   - EnterpriseDashboard EditorialView (inline detail)
 *
 * Accepts `blocks=[{type, ...}]` and returns a styled article body. If `blocks` is
 * empty/undefined we fall back to legacy markdown via `<ReactMarkdown>`.
 *
 * Block types supported:
 *   - headline       { level: 1|2|3, text }
 *   - standfirst     { text }
 *   - text           { text } (markdown allowed)
 *   - pullquote      { text }
 *   - callout        { variant: "info"|"warning"|"success", text }
 *   - image          { url, caption?, alt? }
 *   - divider        {}
 *   - chart_tracker  { tracker_id, caption? }
 *   - chart_snapshot { snapshot_id, caption? }
 *   - chart_wave_question { wave_id, question_id, caption? }
 */
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import { useLanguage } from "../hooks/useLanguage";

const CHART_COLORS = ["#16A34A", "#0A0A0A", "#0055FF", "#F4C430", "#8B5CF6", "#14B8A6", "#DC2626"];

// ITER 67 — Pick the Urdu translation of an option's label when the current
// language is Urdu AND the option has a non-empty `text_ur`; else fall back
// to `text` / `key`. Backend snapshot/tracker payloads already carry
// `text_ur` next to every option (see routers/i18n.py: option translation).
function optionLabel(opt, lang) {
  if (!opt) return "";
  if (lang === "ur" && (opt.text_ur || "").trim()) return opt.text_ur;
  return opt.text || opt.key || "";
}

function HeadlineBlock({ block }) {
  const level = block.level || 1;
  const cls = level === 1
    ? "text-3xl md:text-4xl font-black tracking-tighter mt-12 mb-4"
    : level === 2
      ? "text-2xl md:text-3xl font-black tracking-tight mt-10 mb-3"
      : "text-xl md:text-2xl font-bold tracking-tight mt-8 mb-2";
  const Tag = `h${level + 1}`; // article title is h1, so blocks start at h2
  return <Tag className={cls}>{block.text}</Tag>;
}

function StandfirstBlock({ block }) {
  return (
    <p className="text-xl md:text-2xl font-serif leading-snug text-neutral-700 dark:text-neutral-300 italic mt-6 mb-8 border-l-4 border-mango pl-5">
      {block.text}
    </p>
  );
}

function TextBlock({ block }) {
  return (
    <div className="prose prose-neutral dark:prose-invert max-w-none text-lg leading-relaxed my-4" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.text || ""}</ReactMarkdown>
    </div>
  );
}

function PullQuoteBlock({ block }) {
  return (
    <blockquote className="my-10 border-y-2 border-black py-6 text-2xl md:text-3xl font-black tracking-tight leading-snug text-center">
      &ldquo;{block.text}&rdquo;
    </blockquote>
  );
}

function CalloutBlock({ block }) {
  const variants = {
    info: "bg-blue-50 border-blue-600 text-blue-900",
    warning: "bg-amber-50 border-amber-600 text-amber-900",
    success: "bg-green-50 border-green-700 text-green-900",
  };
  const cls = variants[block.variant || "info"] || variants.info;
  return (
    <div className={`my-6 border-l-4 p-5 ${cls}`}>
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.text || ""}</ReactMarkdown>
      </div>
    </div>
  );
}

function ImageBlock({ block }) {
  if (!block.url) return null;
  return (
    <figure className="my-8">
      <img src={block.url} alt={block.alt || ""} className="w-full h-auto" />
      {block.caption && (
        <figcaption className="mt-2 text-xs text-neutral-500 font-mono uppercase tracking-wider text-center">{block.caption}</figcaption>
      )}
    </figure>
  );
}

function DividerBlock() {
  return <hr className="my-10 border-t-2 border-black" />;
}

// ---------------- Chart blocks ----------------

function ChartCaption({ caption, n }) {
  if (!caption && !n) return null;
  return (
    <figcaption className="mt-2 text-xs text-neutral-500 font-mono uppercase tracking-wider text-center">
      {caption}{caption && n ? " · " : ""}{n ? `n = ${n}` : ""}
    </figcaption>
  );
}

function ChartTrackerBlock({ block }) {
  const { lang, t } = useLanguage();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!block.tracker_id) return;
    api.get(`/trackers/${block.tracker_id}/data`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [block.tracker_id]);
  if (!data) return <div className="my-8 h-64 bg-neutral-100 dark:bg-neutral-900 animate-pulse" />;
  if (data.error || !data.series?.length) return <div className="my-8 p-5 border border-dashed border-neutral-300 text-sm text-neutral-500">{t("ui_ur_no_tracker_data")}</div>;
  // Tracker.name_ur is populated when approved; otherwise fall back to English.
  const trackerName = (lang === "ur" && (data.tracker?.name_ur_status === "approved") && (data.tracker?.name_ur || "").trim())
    ? data.tracker.name_ur
    : (data.tracker?.name || t("ui_ur_tracker"));
  return (
    <figure className="my-10 border-2 border-black p-5 bg-white">
      <div className="overline text-mango mb-3">{t("ui_ur_chart")} · {trackerName}</div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data.series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit="%" />
          <Tooltip />
          <Legend />
          {(data.options || []).map((opt, i) => (
            <Line key={opt.key} type="monotone" dataKey={opt.key} name={optionLabel(opt, lang)} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <ChartCaption caption={block.caption} />
    </figure>
  );
}

function ChartSnapshotBlock({ block }) {
  const { lang, t } = useLanguage();
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    if (!block.snapshot_id) return;
    api.get(`/public/snapshots/${block.snapshot_id}`)
      .then((r) => setSnap(r.data))
      .catch(() => setSnap({ error: true }));
  }, [block.snapshot_id]);
  if (!snap) return <div className="my-8 h-64 bg-neutral-100 dark:bg-neutral-900 animate-pulse" />;
  if (snap.error) return <div className="my-8 p-5 border border-dashed border-neutral-300 text-sm text-neutral-500">{t("ui_ur_no_snapshot_data")}</div>;
  const overall = snap.overall || {};
  // Map option keys (`opt_1`, `opt_2`, …) → human text (Urdu if approved) so charts NEVER render raw IDs.
  const optMap = Object.fromEntries((snap.options || []).map((o) => [o.key, optionLabel(o, lang)]));
  const series = Object.entries(overall).map(([k, v]) => ({
    label: optMap[k] || k,
    pct: typeof v === "number" ? (v <= 1 ? +(v * 100).toFixed(1) : +v.toFixed(1)) : 0,
  }));
  const qText = (lang === "ur" && (snap.question_text_ur || "").trim()) ? snap.question_text_ur : snap.question_text;
  return (
    <figure className="my-10 border-2 border-black p-5 bg-white">
      <div className="overline text-mango mb-3">{t("ui_ur_snapshot_overline")}</div>
      <h3 className="font-bold text-lg mb-3">{qText}</h3>
      <ResponsiveContainer width="100%" height={Math.max(220, series.length * 36)}>
        <BarChart data={series} layout="vertical" margin={{ left: 100 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
          <XAxis type="number" unit="%" tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={100} />
          <Tooltip />
          <Bar dataKey="pct" name="%">
            {series.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartCaption caption={block.caption} n={snap.n_sample} />
    </figure>
  );
}

function ChartWaveQuestionBlock({ block }) {
  const { lang, t } = useLanguage();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!block.wave_id || !block.question_id) return;
    api.get(`/articles/chart-data/wave-question?wave_id=${encodeURIComponent(block.wave_id)}&question_id=${encodeURIComponent(block.question_id)}`)
      .then((r) => setD(r.data))
      .catch(() => setD({ error: true }));
  }, [block.wave_id, block.question_id]);
  if (!d) return <div className="my-8 h-64 bg-neutral-100 dark:bg-neutral-900 animate-pulse" />;
  if (d.error || !d.data?.length) return <div className="my-8 p-5 border border-dashed border-neutral-300 text-sm text-neutral-500">{t("ui_ur_no_data")}</div>;
  // Backend returns both `label` and `label_ur` (approved Urdu variant).
  const rows = d.data.map((r) => ({
    ...r,
    label: (lang === "ur" && (r.label_ur || "").trim()) ? r.label_ur : r.label,
  }));
  const qText = (lang === "ur" && (d.question_text_ur || "").trim()) ? d.question_text_ur : d.question_text;
  return (
    <figure className="my-10 border-2 border-black p-5 bg-white">
      <div className="overline text-mango mb-3">{t("ui_ur_wave_result")}</div>
      <h3 className="font-bold text-lg mb-3">{qText}</h3>
      <ResponsiveContainer width="100%" height={Math.max(220, rows.length * 36)}>
        <BarChart data={rows} layout="vertical" margin={{ left: 100 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
          <XAxis type="number" unit="%" tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={100} />
          <Tooltip />
          <Bar dataKey="pct" name="%">
            {rows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartCaption caption={block.caption} n={d.n} />
    </figure>
  );
}

const BLOCK_COMPONENTS = {
  headline: HeadlineBlock,
  standfirst: StandfirstBlock,
  text: TextBlock,
  pullquote: PullQuoteBlock,
  callout: CalloutBlock,
  image: ImageBlock,
  divider: DividerBlock,
  chart_tracker: ChartTrackerBlock,
  chart_snapshot: ChartSnapshotBlock,
  chart_wave_question: ChartWaveQuestionBlock,
};

/**
 * ITER 66 — Project one block to a target language. When lang="ur", replace
 * every translatable field with its `_ur` sibling if that sibling is
 * approved AND non-empty. Missing / draft / stale silently fall back to
 * English so the reader never sees a half-translated block.
 */
function projectBlock(block, lang) {
  if (!block || lang !== "ur") return block;
  const fields = ["text", "caption", "alt"];
  const out = { ...block };
  fields.forEach((f) => {
    const status = block[`${f}_ur_status`];
    const ur = block[`${f}_ur`];
    if (status === "approved" && (ur || "").trim()) {
      out[f] = ur;
    }
  });
  return out;
}

/**
 * Render an article body. Prefers `blocks[]`; falls back to markdown `body`.
 * When `lang="ur"`, each block is projected to its approved Urdu sibling
 * (fields missing an approved translation stay in English).
 */
export default function ArticleBlocks({ blocks, body, lang = "en" }) {
  // Block path — preferred when present and non-empty.
  if (Array.isArray(blocks) && blocks.length > 0) {
    return (
      <div data-testid="article-blocks" dir={lang === "ur" ? "rtl" : undefined}>
        {blocks.map((rawB, i) => {
          const b = projectBlock(rawB, lang);
          const Cmp = BLOCK_COMPONENTS[b.type];
          if (!Cmp) return null;
          return <div key={i} data-testid={`article-block-${b.type}`}><Cmp block={b} /></div>;
        })}
      </div>
    );
  }
  // Legacy markdown fallback (for articles that haven't been migrated yet).
  return (
    <div className="prose prose-neutral dark:prose-invert max-w-none text-lg leading-relaxed" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} dir={lang === "ur" ? "rtl" : undefined}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || ""}</ReactMarkdown>
    </div>
  );
}
